import React, { useReducer, useMemo } from 'react';
import { useFormState } from 'react-hook-form';
import { FormActions } from '@packages/ui/patterns';
import { Button, Checkbox, Typography } from '@packages/ui/primitives';
import { Stack } from '@packages/ui/layout';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import type { StepProcessorReturn } from '../processors/stepProcessor';
import type { StepAcknowledgement } from '../types/step';

interface FormStepperBarProps {
  stepProcessor: StepProcessorReturn;
  isLastStep: boolean;
  hasSubmit: boolean;
}

interface AcknowledgementState {
  checkedByStep: Record<string, Record<string, boolean>>;
}

type AcknowledgementAction =
  | { type: 'SET_ACK'; stepId: string; ackId: string; value: boolean };

function acknowledgementReducer(
  state: AcknowledgementState,
  action: AcknowledgementAction,
): AcknowledgementState {
  switch (action.type) {
    case 'SET_ACK': {
      const stepMap = state.checkedByStep[action.stepId] ?? {};
      return {
        checkedByStep: {
          ...state.checkedByStep,
          [action.stepId]: {
            ...stepMap,
            [action.ackId]: action.value,
          },
        },
      };
    }
    default: {
      const _exhaustive: never = action;
      return state;
    }
  }
}

function areRequiredAcknowledgementsChecked(
  acknowledgements: StepAcknowledgement[] | undefined,
  stepId: string,
  checkedByStep: Record<string, Record<string, boolean>>,
): boolean {
  if (!acknowledgements?.length) return true;

  const stepChecked = checkedByStep[stepId] ?? {};
  return acknowledgements.every((ack) => {
    if (ack.required === false) return true;
    return stepChecked[ack.id] === true;
  });
}

export const FormStepperBar = React.memo(function FormStepperBar({
  stepProcessor,
  isLastStep,
  hasSubmit,
}: FormStepperBarProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const { isReadOnly } = useFormEngineState();
  const { isSubmitting } = useFormState({ control: form.control });

  const [ackState, dispatchAck] = useReducer(acknowledgementReducer, {
    checkedByStep: {},
  });

  const { currentStep } = stepProcessor;
  const acknowledgements = currentStep.acknowledgements;

  const allAcknowledgementsChecked = useMemo(
    () => areRequiredAcknowledgementsChecked(
      acknowledgements,
      currentStep.id,
      ackState.checkedByStep,
    ),
    [acknowledgements, currentStep.id, ackState.checkedByStep],
  );

  const nextDisabled = stepProcessor.isTransitioning || !allAcknowledgementsChecked;
  const nextLabel = currentStep.onNext?.label ?? 'Next';
  const secondaryAction = currentStep.onNext?.secondaryAction;

  return (
    <Stack gap='12px'>
      {acknowledgements?.length ? (
        <Stack gap='8px' style={{ padding: '0 0 4px' }}>
          {acknowledgements.map((ack) => {
            const checked = ackState.checkedByStep[currentStep.id]?.[ack.id] ?? false;
            return (
              <Checkbox
                key={ack.id}
                id={`ack-${currentStep.id}-${ack.id}`}
                checked={checked}
                onChange={(event) => {
                  dispatchAck({
                    type: 'SET_ACK',
                    stepId: currentStep.id,
                    ackId: ack.id,
                    value: event.target.checked,
                  });
                }}
                label={
                  <Typography variant='body-s' as='span'>
                    {ack.label}
                  </Typography>
                }
              />
            );
          })}
        </Stack>
      ) : null}

      <FormActions sticky align='between' semanticId='formEngineStepActions'>
        <div>
          {stepProcessor.canGoBack && (
            <Button
              variant='secondary'
              icon='arrow_back'
              label='Back'
              onClick={stepProcessor.goBack}
              disabled={stepProcessor.isTransitioning}
              type='button'
            />
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          {secondaryAction && !isLastStep && (
            <Button
              variant='secondary'
              label={secondaryAction.label}
              onClick={() => stepProcessor.executeSecondaryAction()}
              disabled={stepProcessor.isTransitioning}
              type='button'
            />
          )}

          {!isLastStep && (
            <Button
              variant='primary'
              label={stepProcessor.isTransitioning ? 'Processing...' : nextLabel}
              trailingIcon='arrow_forward'
              onClick={() => stepProcessor.goNext()}
              disabled={nextDisabled}
              type='button'
            />
          )}

          {isLastStep && !isReadOnly && hasSubmit && (
            <Button
              variant='primary'
              type='submit'
              loading={isSubmitting}
              disabled={isSubmitting || !allAcknowledgementsChecked}
              label={isSubmitting ? 'Saving...' : 'Submit'}
            />
          )}
        </div>
      </FormActions>
    </Stack>
  );
});
